import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import type { MainColorsType } from '~/types/components'
import type { SwipeActionColor, SwipeActionPropsType, SwipeOptionType } from '~/types/SwipeAction'

const elementPositions = new Map<string | number, number>()

const MAIN_COLORS: readonly MainColorsType[] = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'default'] as const

/** Fondo solido por color semantico (tokens). */
const COLOR_BG: Readonly<Record<MainColorsType, string>> = {
  primary: 'bg-primary text-white',
  secondary: 'bg-slate-500 text-white',
  success: 'bg-success text-white',
  danger: 'bg-danger text-white',
  warning: 'bg-warning text-white',
  info: 'bg-info text-white',
  default: 'bg-inverse text-on-inverse'
} as const

/** Resuelve un color de acción a clases: nombre semántico → token; si no, se usa tal cual (clase cruda). */
const resolveColor = (color: SwipeActionColor | undefined, fallback: MainColorsType): string => {
  if (!color) return COLOR_BG[fallback]
  if ((MAIN_COLORS as readonly string[]).includes(color)) return COLOR_BG[color as MainColorsType]
  return `text-white ${color}`
}

const SwipeAction = (props: SwipeActionPropsType) => {
  /** Ancho de cada botón de acción. */
  const aw = () => props.actionWidth ?? 72

  let containerRef: HTMLDivElement | undefined
  let contentRef: HTMLDivElement | undefined

  const getInitialPosition = () => {
    if (props.itemId && props.rememberPosition && elementPositions.has(props.itemId)) {
      return elementPositions.get(props.itemId)!
    }
    return 0
  }

  const [translateX, setTranslateX] = createSignal(getInitialPosition())

  let gestureBase = getInitialPosition()
  let gestureLeftMax = 0
  let gestureRightMax = 0
  let hasMouseMoved = false
  let swipeDirection: 'horizontal' | 'vertical' | 'undetermined' = 'undetermined'
  let initialTouchY = 0
  let wasMenuOpenOnDown = false

  const preventSelection = (e: Event) => {
    e.preventDefault()
  }

  const [state, setState] = createStore({
    startX: 0,
    currentX: 0,
    swiping: false,
    containerWidth: 0,
    showMenu: false,
    activeSide: null as 'left' | 'right' | null,
    lastPosition: getInitialPosition()
  })

  onMount(() => {
    if (containerRef) {
      setState('containerWidth', containerRef.offsetWidth)
    }

    window.addEventListener('resize', handleResize)
  })

  onCleanup(() => {
    window.removeEventListener('resize', handleResize)
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('selectstart', preventSelection)
    if (contentRef) {
      contentRef.removeEventListener('click', handleClickCapture, true)
    }
  })

  const handleResize = () => {
    if (containerRef) {
      setState('containerWidth', containerRef.offsetWidth)
      setTranslateX(0)
    }
  }

  const handleClickCapture = (e: Event) => {
    if (props.disabled) return
    if (hasMouseMoved || wasMenuOpenOnDown || state.showMenu || translateX() !== 0) {
      e.stopPropagation()
      e.preventDefault()
      return
    }
  }

  createEffect(() => {
    if (contentRef) {
      contentRef.addEventListener('click', handleClickCapture, true)
    }
  })

  const beginGesture = (clientX: number) => {
    const tx = translateX()
    gestureBase = tx
    wasMenuOpenOnDown = state.showMenu || tx !== 0
    setState({ startX: clientX, currentX: clientX, swiping: true, showMenu: false, activeSide: null, lastPosition: tx })
    gestureLeftMax = (props.leftOptions?.length || 0) * aw()
    gestureRightMax = (props.rightOptions?.length || 0) * aw()
  }

  const updateFromPointer = (clientX: number) => {
    setState('currentX', clientX)
    const rawDiff = clientX - state.startX
    let newTranslateX = gestureBase + rawDiff

    // Resistencia elástica más allá del máximo abierto.
    if (newTranslateX > gestureLeftMax) {
      newTranslateX = gestureLeftMax + (newTranslateX - gestureLeftMax) * 0.35
    }
    if (newTranslateX < -gestureRightMax) {
      newTranslateX = -(gestureRightMax + (Math.abs(newTranslateX) - gestureRightMax) * 0.35)
    }

    const maxAllowed = gestureLeftMax + aw()
    const minAllowed = -gestureRightMax - aw()
    setTranslateX(Math.max(Math.min(newTranslateX, maxAllowed), minAllowed))
  }

  const handleTouchStart = (e: TouchEvent) => {
    if (props.disabled) return
    const touch = e.touches[0]
    swipeDirection = 'undetermined'
    initialTouchY = touch.clientY
    hasMouseMoved = false
    beginGesture(touch.clientX)
  }

  const handleMouseDown = (e: MouseEvent) => {
    if (props.disabled) return
    e.preventDefault()
    if (e.button !== 0) return
    hasMouseMoved = false
    beginGesture(e.clientX)

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('selectstart', preventSelection)
  }

  const handleMouseUp = (_e: Event) => {
    if (!state.swiping || props.disabled) return
    setState('swiping', false)

    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    document.removeEventListener('selectstart', preventSelection)

    if (!hasMouseMoved) {
      if (wasMenuOpenOnDown) closeAllActions()
      return
    }

    finalizeSwipe()
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!state.swiping || props.disabled) return

    const touch = e.touches[0]
    const currentY = touch.clientY

    if (swipeDirection === 'undetermined') {
      const deltaX = Math.abs(touch.clientX - state.startX)
      const deltaY = Math.abs(currentY - initialTouchY)
      const minMovement = 10

      if (deltaX > minMovement || deltaY > minMovement) {
        hasMouseMoved = true
        if (deltaX > deltaY * 1.5) {
          swipeDirection = 'horizontal'
        } else if (deltaY > deltaX * 1.5) {
          swipeDirection = 'vertical'
          setState('swiping', false)
          return
        }
      }
    }

    if (swipeDirection === 'vertical') return

    updateFromPointer(touch.clientX)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!state.swiping || props.disabled) return
    if (Math.abs(e.clientX - state.startX) > 3) hasMouseMoved = true
    updateFromPointer(e.clientX)
  }

  const handleTouchEnd = (_e: TouchEvent) => {
    if (!state.swiping || props.disabled) return
    setState('swiping', false)

    if (swipeDirection === 'vertical') {
      swipeDirection = 'undetermined'
      initialTouchY = 0
      return
    }

    if (!hasMouseMoved && swipeDirection === 'undetermined') {
      if (wasMenuOpenOnDown) closeAllActions()
      swipeDirection = 'undetermined'
      initialTouchY = 0
      return
    }

    finalizeSwipe()
    swipeDirection = 'undetermined'
    initialTouchY = 0
  }

  const openSide = (target: number, side: 'left' | 'right') => {
    setTranslateX(target)
    setState({ showMenu: true, activeSide: side })
    savePosition(target)
    gestureBase = target
    setState('lastPosition', target)
  }

  const finalizeSwipe = () => {
    const currentTranslate = translateX()
    const leftCount = props.leftOptions?.length || 0
    const rightCount = props.rightOptions?.length || 0
    const leftMaxWidth = leftCount * aw()
    const rightMaxWidth = rightCount * aw()
    const fullSwipe = props.fullSwipe !== false

    const snapPoints: number[] = [0]
    for (let i = 1; i <= leftCount; i++) snapPoints.push(i * aw())
    for (let i = 1; i <= rightCount; i++) snapPoints.push(-i * aw())

    const gestureDistance = Math.abs(state.currentX - state.startX)
    const isSmallGesture = gestureDistance < aw() * 0.3

    let nearest = snapPoints[0]
    let nearestDist = Math.abs(currentTranslate - nearest)
    for (const p of snapPoints) {
      const d = Math.abs(currentTranslate - p)
      if (d < nearestDist) {
        nearest = p
        nearestDist = d
      }
    }

    const isNearCenter = Math.abs(currentTranslate) < aw() / 2
    const shouldForceClose = isNearCenter || (isSmallGesture && Math.abs(currentTranslate) < aw() * 1.2) || (nearest === 0 && gestureDistance < aw() * 0.8)

    if (shouldForceClose) {
      resetPosition()
      return
    }

    if (nearest > 0) {
      if (leftCount === 1) {
        if (fullSwipe && nearest >= leftMaxWidth && Math.abs(currentTranslate) >= leftMaxWidth * 0.8) {
          props.leftOptions?.[0]?.action()
          if (props.itemId) elementPositions.delete(props.itemId)
          resetPosition()
          return
        }
        const target = currentTranslate >= leftMaxWidth / 2 ? leftMaxWidth : 0
        if (target !== 0) openSide(target, 'left')
        else resetPosition()
        return
      }

      openSide(nearest, 'left')
      return
    }

    if (nearest < 0) {
      if (rightCount === 1) {
        if (fullSwipe && Math.abs(nearest) >= rightMaxWidth && Math.abs(currentTranslate) >= rightMaxWidth * 0.8) {
          props.rightOptions?.[0]?.action()
          if (props.itemId) elementPositions.delete(props.itemId)
          resetPosition()
          return
        }
        const target = Math.abs(currentTranslate) >= rightMaxWidth / 2 ? -rightMaxWidth : 0
        if (target !== 0) openSide(target, 'right')
        else resetPosition()
        return
      }

      openSide(nearest, 'right')
      return
    }

    resetPosition()
  }

  const resetPosition = () => {
    setTranslateX(0)
    setState({ lastPosition: 0, showMenu: false, activeSide: null })
    gestureBase = 0
    if (props.itemId) elementPositions.delete(props.itemId)
  }

  const savePosition = (position: number) => {
    if (props.itemId && props.rememberPosition) {
      elementPositions.set(props.itemId, position)
      setState('lastPosition', position)
      gestureBase = position
    }
  }

  const handleOptionClick = (option: SwipeOptionType) => {
    option.action()
    setState({ showMenu: false, activeSide: null })
    resetPosition()
  }

  const closeAllActions = () => {
    setTranslateX(0)
    gestureBase = 0
    setState({ lastPosition: 0, showMenu: false, activeSide: null })
    if (props.itemId) elementPositions.delete(props.itemId)
  }

  createEffect(() => {
    if (contentRef) {
      const transition = state.swiping ? 'none' : props.bounceEffect ? 'transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)'
      contentRef.style.transform = `translateX(${translateX()}px)`
      contentRef.style.transition = transition
    }
  })

  const calculateOpacity = (value: number) => Math.min(Math.max(Math.abs(value) / aw(), 0), 1)

  const leftOpacity = createMemo(() => (translateX() > 0 ? calculateOpacity(translateX()) : 0))
  const rightOpacity = createMemo(() => (translateX() < 0 ? calculateOpacity(translateX()) : 0))

  const handleClick = (e: MouseEvent | TouchEvent | Event) => {
    if (props.disabled) return
    // Un clic sobre un control (checkbox, botón, enlace...) no debe contar como
    // "abrir la tarjeta": si llega aquí burbujeando, el control ya hizo su parte.
    const target = e.target as HTMLElement | null
    if (target?.closest('input, button, a, select, textarea, label')) return
    props.onClick?.(e)
  }

  return (
    <div ref={containerRef} class="relative overflow-hidden w-full" style={{ 'touch-action': 'pan-y' }}>
      {/* Acciones izquierdas */}
      <Show when={props.leftOptions?.length}>
        <div class="absolute inset-y-0 left-0 flex items-stretch" style={{ width: `${aw() * (props.leftOptions?.length || 0)}px`, opacity: leftOpacity(), 'z-index': 5 }}>
          {props.leftOptions?.map(option => (
            <button
              type="button"
              style={{ width: `${aw()}px` }}
              class={twMerge('h-full flex flex-col items-center justify-center gap-1 text-2xl', resolveColor(option.color, 'success'))}
              aria-label={option.label}
              title={option.label}
              onClick={() => handleOptionClick(option)}
            >
              <Icon icon={option.icon} size={22} />
              <Show when={option.label}>
                <span class="text-[11px] font-medium leading-none">{option.label}</span>
              </Show>
            </button>
          ))}
        </div>
      </Show>

      {/* Acciones derechas */}
      <Show when={props.rightOptions?.length}>
        <div class="absolute inset-y-0 right-0 flex items-stretch" style={{ width: `${aw() * (props.rightOptions?.length || 0)}px`, opacity: rightOpacity(), 'z-index': 5 }}>
          {props.rightOptions?.map(option => (
            <button
              type="button"
              style={{ width: `${aw()}px` }}
              class={twMerge('h-full flex flex-col items-center justify-center gap-1 text-2xl', resolveColor(option.color, 'danger'))}
              aria-label={option.label}
              title={option.label}
              onClick={() => handleOptionClick(option)}
            >
              <Icon icon={option.icon} size={22} />
              <Show when={option.label}>
                <span class="text-[11px] font-medium leading-none">{option.label}</span>
              </Show>
            </button>
          ))}
        </div>
      </Show>

      {/* Contenido principal */}
      <div
        ref={contentRef}
        class="relative z-10 bg-surface select-none"
        style={{
          'z-index': 10,
          'user-select': 'none',
          cursor: state.swiping ? 'grabbing' : props.onClick ? 'pointer' : 'grab'
        }}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        {props.children}
      </div>
    </div>
  )
}

export default SwipeAction
